import { createStore, applyMiddleware, compose } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { createLogger } from 'redux-logger';
import expertPortal from './reducers';
import { liveQueryMiddleware } from './liveQueryMiddleware/liveQueryMiddlewareGraphQL';
import './styles/libs/bootstrap/css/bootstrap.min.css';
import './styles/index.css';
import INITIAL_STATE from './initialStaticState';
import { persistStore, persistReducer } from 'redux-persist';
import { debugOn } from './util/url';
import { getUserId } from './util/parseSelectors';
import storage from 'redux-persist/lib/storage';
import { SPECIALIST, ACCOUNT_USER } from './constants/roles';
import { handleError } from './services/ErrorService/ErrorService';
import AuthService from './services/authService';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const loggerMiddleware = createLogger();

const getInitialState = () => {
  const user = AuthService.current();

  if (!user?.simplrExpert) {
    return INITIAL_STATE;
  }

  // persist the user id so that the state isn't persisted if a different user logs in
  storage.setItem('simplr:userId', user.id);

  INITIAL_STATE.user.loggedIn = true;
  const roles = user.roles;
  const isAccountUser = Array.isArray(roles) ? roles.includes(ACCOUNT_USER) : false;
  const isAdmin = Array.isArray(roles) ? roles.includes(SPECIALIST) || isAccountUser : false;
  INITIAL_STATE.user.isAccountUser = isAccountUser;
  INITIAL_STATE.user.isAdmin = isAdmin;

  return INITIAL_STATE;
};

const persistConfig = {
  key: 'expert-portal',
  storage,
  whitelist: [
    'allFilters',
    'selectedHistoricalTicketId',
    'selectedNominatedTicketId',
    'selectedAssignedTicketId',
    'selectedAllTicketId',
    'dateRange',
    'escalatedTabFilters',
    'authReducer',
  ],
};

const configureStoreAndPersist = () => {
  const middlewares = [thunkMiddleware];
  if (debugOn()) {
    middlewares.push(loggerMiddleware);
  }

  middlewares.push(liveQueryMiddleware());

  const persistedReducer = persistReducer(persistConfig, expertPortal);
  const store = createStore(persistedReducer, getInitialState(), composeEnhancers(applyMiddleware(...middlewares)));
  const persistor = persistStore(store);

  if (module.hot) {
    module.hot.accept('./reducers', () => {
      store.replaceReducer(require('./reducers').default);
    });
  }

  return { store, persistor };
};

const storeAndPersist = configureStoreAndPersist();
const { store } = storeAndPersist;

export default storeAndPersist;

export const persistor = storeAndPersist.persistor;
// special getState function used by analytics
export const _getState = () => {
  return store.getState();
};

// clear out our persist if user has changed
const purgeIfUserChanged = async () => {
  const lastUserId = await storage.getItem('simplr:userId');
  const currentUserId = getUserId();
  if (currentUserId && currentUserId !== lastUserId) {
    await persistor.purge();
  }
};

purgeIfUserChanged().catch((error) => handleError({ error }));
